/** The approval queue — where money actually becomes access.
 *
 * A super-admin works down this list with a bank statement open beside it.
 * Everything here exists to make one comparison quick and safe: does a
 * credit on the statement match this row?
 *
 * So each row leads with the three things that identify a payment — who,
 * how much, and the reference they were asked to quote — and shows what
 * the learner SAID they paid with, clearly marked as their claim rather
 * than as evidence. Approving grants a real subscription, so the button
 * is deliberately not the easiest thing to hit by accident: rejecting
 * requires a reason, and approving asks for an optional note that ends up
 * on the order.
 */
import { useEffect, useState } from "react";
import * as api from "../../api/billing";
import type { PendingOrder } from "../../api/billing";
import { ApiError } from "../../api/http";
import { Button, Card, EmptyState, Input, Spinner } from "../../components/ui";
import { useToast } from "../../components/ToastContext";
import { useT } from "../../i18n";
import { rupees } from "../../utils/rupees";

export function PaymentsPage() {
  const t = useT();
  const { push } = useToast();
  const [rows, setRows] = useState<PendingOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => {
    api.pendingOrders()
      .then(setRows)
      // An empty queue and an unreachable one look the same here on
      // purpose: there is nothing an admin can do about either, and a
      // spinner that never stops is worse than "nothing waiting".
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const fail = (err: unknown) =>
    push("error", err instanceof ApiError ? err.message : t("common.error.generic"));

  const approve = async (order: PendingOrder) => {
    setBusy(order.id);
    try {
      await api.approveOrder(order.id, notes[order.id] || undefined);
      push("success", t("approve.approved"));
      setRows((prev) => prev.filter((r) => r.id !== order.id));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const reject = async (order: PendingOrder) => {
    // No guard on an empty reason here: the button that calls this is
    // disabled without one, and the service refuses one anyway.
    setBusy(order.id);
    try {
      await api.rejectOrder(order.id, reason.trim());
      push("success", t("approve.rejected"));
      setRows((prev) => prev.filter((r) => r.id !== order.id));
      setRejecting(null);
      setReason("");
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="grid place-items-center py-10">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-ink">{t("approve.title")}</h1>
        <p className="mt-1 text-sm text-muted">{t("approve.subtitle")}</p>
      </div>

      {rows.length === 0 ? (
        <EmptyState message={t("approve.none")} />
      ) : (
        <ul className="space-y-3">
          {rows.map((order) => (
            <li key={order.id}>
              <Card>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-ink">
                      {order.tenant_name ?? order.tenant_id}
                    </p>
                    <p className="mt-0.5 text-xs text-muted">
                      {order.plan_code} · {order.period} ·{" "}
                      {order.created_at
                        ? new Date(order.created_at).toLocaleDateString("en-IN")
                        : ""}
                    </p>
                  </div>
                  <p className="text-lg font-extrabold text-ink">
                    {rupees(order.amount_minor)}
                  </p>
                </div>

                <dl className="mt-3 space-y-1 text-sm">
                  <div className="flex flex-wrap justify-between gap-2">
                    <dt className="text-muted">{t("approve.reference")}</dt>
                    <dd className="font-mono font-bold text-ink">
                      {order.reference}
                    </dd>
                  </div>
                  <div className="flex flex-wrap justify-between gap-2">
                    <dt className="text-muted">{t("approve.claimed")}</dt>
                    {/* Marked as a claim, not as evidence — it is typed in
                        by the person asking for access. */}
                    <dd className="font-mono text-ink">
                      {order.payer_reference || (
                        <span className="font-sans italic text-muted">
                          {t("approve.notReported")}
                        </span>
                      )}
                    </dd>
                  </div>
                </dl>

                {rejecting === order.id ? (
                  <div className="mt-3 space-y-2">
                    <Input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={t("approve.reasonPlaceholder")}
                      aria-label={t("approve.reasonLabel")}
                    />
                    <p className="text-xs text-muted">{t("approve.reasonRequired")}</p>
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button variant="ghost" onClick={() => setRejecting(null)}>
                        {t("common.cancel")}
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => reject(order)}
                        loading={busy === order.id}
                        disabled={!reason.trim()}
                      >
                        {t("approve.reject")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 space-y-2">
                    <Input
                      value={notes[order.id] ?? ""}
                      onChange={(e) =>
                        setNotes((prev) => ({ ...prev, [order.id]: e.target.value }))
                      }
                      placeholder={t("approve.notePlaceholder")}
                      aria-label={t("approve.noteLabel")}
                    />
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setRejecting(order.id);
                          setReason("");
                        }}
                      >
                        {t("approve.reject")}
                      </Button>
                      <Button
                        onClick={() => approve(order)}
                        loading={busy === order.id}
                      >
                        {t("approve.approve")}
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
